import {
  faArrowDown as ArrowDownIcon,
  faArrowUp as ArrowUpIcon
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip, Stack, Typography } from '@mui/material';
import { sortBy } from 'lodash';

import { STATUS_COLORS } from '@/constants/chartcolors.constants';
import type { TicketMetricsByPriority } from '@/pages/ReportsTemplates/ReportsTemplate.types';
import type { Ticket } from '@/pages/Tickets';
import { formatTickets } from '@/pages/Tickets/Tickets.utils';
import calcGroupBy from '@/utils/Common.utils';

import type { TicketMetricsBoxType } from '../ReportsPage.types';

export const formatServiceTicketStatusGroup = (tickets: Ticket[]) => {
  const ticketsInTimeFrame = tickets.filter(
    (ticket: Ticket) => ticket.ticket_type === 2
  );
  const results = formatTickets(
    ticketsInTimeFrame,
    'ticket_status.name' as 'ticket_status'
  );

  const statusOrder = Object.keys(STATUS_COLORS);
  return sortBy(results, (result) => statusOrder.indexOf(result.name));
};

export const formatServiceTicketCategoryGroup = (tickets: Ticket[]) => {
  const groupedTickets = calcGroupBy(
    tickets,
    'ticket_category.name' as 'ticket_category',
    10
  );

  return groupedTickets;
};

const ticketBox = (
  title: string,
  total: number,
  change: string,
  id: number
) => {
  return (
    <Stack
      key={id}
      direction="column"
      sx={{ width: '300px', alignItems: 'center' }}
      spacing={2}
    >
      <Typography>{title}</Typography>
      <Typography
        sx={{ fontWeight: 'bold', marginTop: '0 !important' }}
        variant="h2"
      >
        {total}
      </Typography>
      <Chip
        size="medium"
        label={change.includes('-') ? `${change}%` : `+${change}%`}
        variant="twoTone"
        color={change.includes('-') ? 'success' : 'error'}
        sx={{
          backgroundColor:
            change.includes('-') || change === '0.00' ? '#E8F5E9' : '#FECDD2',
          color:
            change.includes('-') || change === '0.00' ? '#1B5E20' : '#8C0000',
          borderRadius: 6,
          marginRight: 2,
          marginTop: '2px !important'
        }}
        icon={
          <FontAwesomeIcon
            icon={
              change.includes('-') || change === '0.00'
                ? ArrowDownIcon
                : ArrowUpIcon
            }
          />
        }
      />
    </Stack>
  );
};

export const ticketsFilledBox = (
  ticketMetrics: TicketMetricsBoxType[],
  title: string
) => {
  return (
    <Stack
      key={title}
      sx={{ alignItems: 'center', flexDirection: 'column' }}
      spacing={2}
    >
      <Typography sx={{ color: '#075B64' }} variant="h6">
        {title}
      </Typography>
      <Stack
        spacing={2}
        direction="row"
        sx={{ width: '100%', justifyContent: 'space-evenly' }}
      >
        {ticketMetrics.map((ticket: TicketMetricsBoxType) =>
          ticketBox(ticket.title, ticket.value, ticket.change, ticket.id)
        )}
      </Stack>
    </Stack>
  );
};

export const getMetricTimes = (
  metric: keyof TicketMetricsByPriority['metrics'],
  metricsData: TicketMetricsByPriority[],
  priorities = [1, 2, 3, 4, 5]
) => {
  const allPriorities: { priority: number; time_to_status_minutes: number }[] =
    [];

  if (!metricsData || metricsData.length === 0) {
    return priorities
      ? priorities.map((priority: number) => ({
          priority,
          time_to_status_minutes: 0
        }))
      : [];
  }

  metricsData?.forEach((item: TicketMetricsByPriority) => {
    if (priorities && !priorities.includes(item.priority_id)) {
      return;
    }
    const priority = item.priority_id;
    const metricValue =
      item.metrics[metric as keyof typeof item.metrics].mean_seconds;
    const timeToStatusMinutes =
      metricValue != null ? (metricValue as number) / 60 : 0;
    allPriorities.push({
      priority,
      time_to_status_minutes: timeToStatusMinutes
    });
  });

  if (allPriorities.length < priorities?.length) {
    priorities?.forEach((priority: number) => {
      if (!allPriorities.some((item) => item.priority === priority)) {
        allPriorities.push({
          priority,
          time_to_status_minutes: 0
        });
      }
    });
  }

  return allPriorities;
};

export const convertSecondsToReadableTime = (
  seconds: number | null | undefined
): string => {
  if (seconds === null || seconds === undefined || seconds < 0) {
    return 'N/A';
  }

  const days = seconds / 86400;
  const hours = seconds / 3600;
  const mins = seconds / 60;

  if (days >= 1) {
    return `${Math.round(days)}d`;
  }
  if (hours >= 1) {
    return `${Math.round(hours)}h`;
  }
  if (mins >= 1) {
    return `${Math.round(mins)}m`;
  }
  return `${Math.round(seconds)}s`;
};

export const transformMetricsToRowData = (
  metricsData: TicketMetricsByPriority[],
  metricKeys: string[] = ['mttd', 'mttc', 'mttn', 'mtta', 'mttrem']
) => {
  const groupMapping: Record<string, string> = {
    mttd: 'Pondurance',
    mttc: 'Pondurance',
    mttn: 'Pondurance',
    mtta: 'Customer',
    mttrem: 'Customer'
  };

  const metricNames: Record<string, string> = {
    mttd: 'Mean Time to Detect',
    mttc: 'Mean Time to Contain',
    mttn: 'Mean Time to Escalate',
    mtta: 'Mean Time to Acknowledge',
    mttrem: 'Mean Time to Remediate'
  };

  const rowData = metricKeys
    .map((metricKey) => {
      const metricsByPriority: Record<string, string | null> = {
        p1: '--',
        p2: '--',
        p3: '--',
        p4: '--',
        p5: '--'
      };

      let metricName = '';

      metricsData?.forEach((item) => {
        const metric = item.metrics[metricKey as keyof typeof item.metrics];
        if (metric) {
          if (!metricName) {
            metricName = metric.name;
          }
          const priorityKey = `p${item.priority_id}`;
          if (Object.hasOwn(metricsByPriority, priorityKey)) {
            metricsByPriority[priorityKey] = convertSecondsToReadableTime(
              metric.mean_seconds
            );
          }
        }
      });

      return {
        metric_name: metricName || metricNames[metricKey] || metricKey,
        ...metricsByPriority,
        group: groupMapping[metricKey] || 'Unknown'
      };
    })
    .filter((row) => row !== null);

  return rowData;
};

export default ticketsFilledBox;
